<template>
  <div v-if="isQuickAction" class="quick_action_item mt-1">
    <div v-if="!loadingQuickAction" @click="startResearchQuickAction">
      <a-tooltip placement="top" title="Run Market Research">
        <ThemeImage class="size-5" :icon="icon" alt="market_research" />
      </a-tooltip>
    </div>
    <div v-else>
      <a-tooltip placement="top" title="Generating Market Research">
        <ThemeImage class="size-5" :icon="icon" alt="market_research" />
      </a-tooltip>
    </div>
  </div>

  <div
    v-else
    :id="`${source}_market_research_section`"
    :class="`${show ? 'flex' : 'hidden'} flex-col gap-4 w-full`"
  >
    <div
      id="research_header"
      :class="['flex flex-row items-center', hideTitle ? 'justify-end' : 'justify-between']"
    >
      <span v-if="!hideTitle" class="text-base font-semibold text_color">Market Research</span>
      <a-button
        class="secondary_small"
        @click="startResearchDetails"
        :disabled="researchStatus === marketResearchStatus.IN_PROGRESS"
        :loading="loading"
      >
        {{ runAgentButtonText }}
      </a-button>
    </div>

    <CustomContainer
      v-if="
        researchStatus === marketResearchStatus.DONE &&
        markdownResearch.length > 0
      "
      class="custom_scroll_conatiner px-2"
      :style="{ lineHeight: 1.2, height: height }"
    >
      <MarkdownContent
        :markdownText="markdownResearch"
        customClass="pb-2 text-[15px]"
      />
    </CustomContainer>

    <CustomContainer
      v-else-if="
        researchStatus === marketResearchStatus.TODO ||
        researchStatus === marketResearchStatus.IN_PROGRESS
      "
    >
      <div style="padding: 10px">
        <div class="flex flex-row">
          <ThemeImage
            class="size-4 mr-1.5"
            :icon="imageNames.AGI_PURPLE"
            alt="note_icon"
          />
          Generating...
        </div>
        <div class="loading_placeholder">
          <div class="loading_line custom_skeleton"></div>
          <div class="loading_line custom_skeleton"></div>
          <div class="loading_line short custom_skeleton"></div>
        </div>
      </div>
    </CustomContainer>

    <EmptyScreen
      v-else-if="
        researchStatus === marketResearchStatus.FAILED ||
        researchStatus === marketResearchStatus.TIMED_OUT
      "
      :source="
        source === 'contact_view'
          ? 'contact_research_tab'
          : 'company_research_tab'
      "
      :text="'Market research generation failed! Please try again.'"
    />

    <EmptyScreen
      v-else
      :source="
        source === 'contact_view'
          ? 'contact_research_tab'
          : 'company_research_tab'
      "
      :text="'No market research generated yet!'"
    />
  </div>

  <!-- Instructions Modal -->
  <InstructionsModal
    :source="source"
    v-model:visible="showInstructionsModal"
    title="Generate Market Research"
    description="Provide custom instructions the agent should follow.(Optional)"
    placeholder="Enter instructions here"
    confirm-button-text="Generate Research"
    :loading="loading"
    @confirm="handleInstructionsConfirm"
  />
</template>

<script setup>
import CustomContainer from "@/src/components/CustomContainer.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import MarkdownContent from "@/src/components/MarkdownContent.vue";
import InstructionsModal from "@/src/components/InstructionsModal.vue";
import DashboardService from "@/src/api/DashboardService.js";
import { message } from "ant-design-vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import eventBus from "@/src/utils/eventBus.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { marketResearchStatus } from "@/src/constants/researchStatusConstants";

const props = defineProps({
  source: { type: String, required: true },
  entityId: { type: [Number, String], required: true },
  height: { type: String, default: "75vh" },
  show: { type: Boolean, default: true },
  isQuickAction: { type: Boolean, default: false },
  isPending: { type: Boolean, default: false },
  hideTitle: { type: Boolean, default: false },
});

const loading = ref(false);
const icon = imageNames.MARKET_RESEARCH_GREY;
const loadingQuickAction = ref(props.isPending);

const researchStatus = ref("");
const markdownResearch = ref("");
const runAgentButtonText = ref("Run Agent");

// Instructions modal state
const showInstructionsModal = ref(false);
const pendingAction = ref(null);

const checkDetailsAndStartResearch = async (
  checkFunction,
  startFunction,
  userInstruction = ""
) => {
  try {
    const response = await checkFunction(props.entityId);
    if (response.success) {
      if (!response.name_present && props.source === "contact_view") {
        message.error("First name is required to run Market Research");
        return;
      }
      if (!response.company_present && props.source === "contact_view") {
        message.error("Add at least one company to run Market Research");
        return;
      }
      if (!response.name_present && props.source === "lead_view") {
        message.error("First name is required to run Market Research");
        return;
      }
      if (!response.company_present && props.source === "lead_view") {
        message.error("Add at least one company to run Market Research");
        return;
      }
      if (!response.url_present && props.source === "company_view") {
        message.error(
          "Configure company name or website to run Market Research"
        );
        return;
      }

      try {
        const startResponse = await startFunction(
          props.entityId,
          userInstruction
        );
        if (startResponse.success) {
          loadingQuickAction.value = true;
          eventBus.emit("research-started", { entityId: props.entityId });
          eventBus.emit("activity-logged");
          message.success("Generating Market Research");
          await fetchResearchDetails();
        } else if (startResponse.message) {
          message.error(startResponse.message);
        } else {
          message.error(
            "An error occurred while generating the Market Research"
          );
        }
      } catch (error) {
        console.error("Error starting research:", error);
        message.error("An error occurred while generating the Market Research");
      }
    } else {
      message.error("An error occurred while generating the Market Research");
    }
  } catch (error) {
    console.error("Error checking details:", error);
    message.error("An error occurred while generating the Market Research");
  }
};

const startContactResearchDetails = async (userInstruction = "") => {
  await checkDetailsAndStartResearch(
    DashboardService.checkContactCompany,
    DashboardService.startContactResearch,
    userInstruction
  );
};

const startCompanyResearchDetails = async (userInstruction = "") => {
  await checkDetailsAndStartResearch(
    DashboardService.checkCompanyWebsite,
    DashboardService.startCompanyResearch,
    userInstruction
  );
};

const startLeadResearchDetails = async (userInstruction = "") => {
  await checkDetailsAndStartResearch(
    DashboardService.checkLeadCompany,
    DashboardService.startLeadResearch,
    userInstruction
  );
};

const showInstructionsModalForAction = (actionType) => {
  pendingAction.value = actionType;
  showInstructionsModal.value = true;
};

const startResearchDetails = async () => {
  loading.value = true;

  // Show instructions modal before starting research
  if (props.source === "contact_view") {
    showInstructionsModalForAction("contact");
  } else if (props.source === "company_view") {
    showInstructionsModalForAction("company");
  } else if (props.source === "lead_view") {
    showInstructionsModalForAction("lead");
  }

  loading.value = false;
};

const handleInstructionsConfirm = async (instructions) => {
  showInstructionsModal.value = false;
  loading.value = true;

  try {
    if (pendingAction.value === "contact") {
      await startContactResearchDetails(instructions);
    } else if (pendingAction.value === "company") {
      await startCompanyResearchDetails(instructions);
    } else if (pendingAction.value === "lead") {
      await startLeadResearchDetails(instructions);
    }
  } finally {
    loading.value = false;
    pendingAction.value = null;
  }
};

const fetchResearchDetails = async () => {
  let fetchFunction;

  if (props.source === "contact_view") {
    fetchFunction = DashboardService.fetchContactResearchDetails;
  } else if (props.source === "company_view") {
    fetchFunction = DashboardService.fetchCompanyResearchDetails;
  } else if (props.source === "lead_view") {
    fetchFunction = DashboardService.fetchLeadResearchDetails;
  } else {
    return;
  }

  try {
    const response = await fetchFunction(props.entityId);
    if (props.isQuickAction) {
      if (response?.success && response?.research_status === "IN_PROGRESS") {
        loadingQuickAction.value = true;
      } else if (response?.success && response?.research_status === "DONE") {
        loadingQuickAction.value = false;
      }
    } else if (response.success) {
      researchStatus.value = response.research_status;
      eventBus.emit("research-status", response.research_status);
      if (response.research_status === "DONE") {
        markdownResearch.value = response.file_content;
        runAgentButtonText.value = "Re-run";
        markdownResearch.value = markdownResearch.value
          .replace(/```markdown\n/, "")
          .replace(/```/, "");
        loadingQuickAction.value = false;
      }
    } else {
      message.error("Failed to fetch Market Research");
    }
  } catch (error) {
    console.error("Error fetching research:", error);
  }
};

const processResearchStatus = async (data) => {
  try {
    await fetchResearchDetails();
    message.success("Market Research Generation Completed");
  } catch (error) {
    researchStatus.value = "FAILED";
    loadingQuickAction.value = false;
    message.error("Market research generation failed! Please try again.");
    console.error("Error fetching research:", error);
  }
};

const startResearchQuickAction = async () => {
  await fetchResearchDetails();
  if (!loadingQuickAction.value) {
    startResearchDetails();
  }
};

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      fetchResearchDetails();
    }
  }
);

watch(
  () => props.isPending,
  (newVal) => {
    loadingQuickAction.value = newVal;
  },
  {
    immediate: true,
  }
);

onMounted(() => {
  eventBus.on("start-market-research", startResearchDetails);
  if (!props.isQuickAction) {
    fetchResearchDetails();
  }
  eventBus.on("research-status-received", processResearchStatus);
});

onUnmounted(() => {
  eventBus.off("start-market-research", startResearchDetails);
  eventBus.off("research-status-received", processResearchStatus);
});
</script>

<style scoped>
.custom_scroll_conatiner {
  overflow-y: auto;
}
.loading_placeholder {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 15px;
}
.loading_line {
  height: 10px;
  background-color: var(--white-opacity-6);
  border-radius: 4px;
}
.loading_line.short {
  width: 75%;
}
</style>
